import React from 'react';
import moment from 'moment';
import capitalize from 'lodash/capitalize';

import tableStyles from '../../../styles/views/components/Table.module.scss';
import styles from '../../../styles/views/pages/AllUpgrades/AllUpgrades.module.scss';
import ForwardIcon from '../../../../assets/icon-arrow-forward-brown-filled.svg';
import FiberNewIcon from '../../../../assets/fiber_new_icon.svg';
import { PackageOrderKeys } from '../../../../../shared/enums';
import { CURRENCIES } from '../../../../../shared/constants';

const columns = {
  projectName: {
    Header: 'ProjectName',
    accessor: 'projectName',
    disableSortBy: true,
  },
  editorNameDOP: {
    Header: 'EditorDOP',
    accessor: 'editorNameDOP',
    disableSortBy: true,
  },
  editorName: {
    Header: 'EditorCurrent',
    accessor: 'editorName',
    disableSortBy: true,
  },
  upgradeRequest: {
    Header: 'UpgradeRequests',
    accessor: 'upgradeRequest',
    disableSortBy: true,
  },
  totalPrice: {
    Header: 'Total',
    accessor: 'totalPrice',
    disableSortBy: true,
  },
  date: {
    Header: 'DateOfCreation',
    accessor: 'date',
    disableSortBy: true,
  },
  status: {
    Header: 'Status',
    accessor: 'status',
    disableSortBy: true,
  },
  expander: {
    id: 'expander',
    Header: ({ getToggleAllRowsExpandedProps, isAllRowsExpanded }) => (
      <span
        {...getToggleAllRowsExpandedProps()}
        data-tip={isAllRowsExpanded ? 'Collapse all' : 'Expand all'}
      >
        {isAllRowsExpanded ? <ForwardIcon className={styles.arrowUp} /> : <ForwardIcon className={styles.arrowDown} />}
      </span>
    ),

    Cell: ({ row }) => (
      <span className={styles.arrowWrapper}>
        {row.isExpanded ? (
          <ForwardIcon className={styles.arrowUp} />
        ) : (
          <ForwardIcon className={styles.arrowDown} />
        )}
      </span>
    ),
    disableSortBy: true,
    customStyles: {
      width: '2%',
      padding: '0 16px 0 0',
    },
  },
};

function getColumns() {
  return Object.values(columns);
}

const checkIsUpgradeNew = (date) => {
  const now = moment();
  const weekBeforeNow = moment().subtract(1, 'weeks');
  if (moment(date).isBetween(weekBeforeNow, now)) return true;

  return false;
};

const makeOrderTitle = (order) => {
  if (order.key === PackageOrderKeys.EXTENSION_FEE) return `${order.title}.`;
  if (!order.quantity) return `${order.title}.`;
  return `${order.quantity} x ${order.title}.`;
};

function getData(upgradeRequests) {
  if (!upgradeRequests?.length) return [];
  return upgradeRequests.map(({
    _id: projectId,
    customer,
    editor,
    upgradeRequests: {
      totalAmount,
      orders,
      createdAt,
      editorDOP,
      status,
      paymentIntentId,
      subscriptionId,
    },
  }) => ({
    projectId,
    projectName: customer?.fullName,
    editorNameDOP: editorDOP?.fullName,
    editorName: editor.fullName,
    upgradeRequest: orders,
    totalPrice: totalAmount,
    status: capitalize(status),
    date: createdAt,
    isNew: checkIsUpgradeNew(createdAt),
    paymentIntentId,
    subscriptionId,
  }));
}

function getExcelData(upgradeRequests) {
  if (!upgradeRequests?.length) return [];

  return upgradeRequests.map(({
    customer,
    editor,
    upgradeRequests: {
      orders,
      createdAt,
      editorDOP,
      status,
      currency,
      totalAmount,
    },
  }) => {
    const currencySign = CURRENCIES.find((c) => c.code === currency).sign;

    return {
      projectName: customer?.fullName,
      editorNameDOP: editorDOP?.fullName,
      editorName: editor.fullName,
      upgradeRequest: orders.map((order) => order.title).join('\n'),
      quantities: orders.map((order) => order.quantity).join('\n'),
      amounts: orders.map((order) => `${currencySign}${order.totalPrice}`).join('\n'),
      totalAmount,
      status: capitalize(status),
      date: moment(createdAt).format('DD MMM YYYY'),
    };
  });
}

const getExcelColumns = () => ([
  'Project name',
  'Editor (Date of payment)',
  'Editor (Current)',
  'Upgrade request line',
  'Quantity',
  'Amount',
  'Total Amount',
  'Status',
  'Date of creation',
]);

function renderCell(cell) {
  const { original: request = {} } = cell?.row || {};
  switch (cell.column.Header) {
    case columns.projectName.Header:
      return (
        <td className={tableStyles.highlightedTableData} style={cell.column.customStyles || {}}>
          {cell.value}
        </td>
      );
    case columns.editorNameDOP.Header:
      return (
        <td className={tableStyles.tableData} style={cell.column.customStyles || {}}>
          {cell.value}
        </td>
      );
    case columns.editorName.Header:
      return (
        <td className={tableStyles.tableData} style={cell.column.customStyles || {}}>
          {cell.value}
        </td>
      );
    case columns.upgradeRequest.Header:
      return (
        <td className={tableStyles.tableData} style={cell.column.customStyles || {}}>
          <span dangerouslySetInnerHTML={{ __html: cell.value.map((order) => makeOrderTitle(order)).join('<br />') }} />
        </td>
      );
    case columns.totalPrice.Header:
      return (
        <td className={tableStyles.tableData} style={cell.column.customStyles || {}}>
          {cell.value}
        </td>
      );
    case columns.date.Header:
      return (
        <td className={tableStyles.tableData} style={cell.column.customStyles || {}}>
          <span>
            {moment(cell.value).format('DD MMM YYYY')}&#8195;
            {request.isNew && <FiberNewIcon data-tip={t('newIconTooltip')} />}
          </span>
        </td>
      );
    case columns.status.Header:
      return (
        <td className={tableStyles.tableData} style={cell.column.customStyles || {}}>
          {cell.value}
        </td>
      );
    default:
      return (
        <td className={tableStyles.tableData} style={cell.column.customStyles || {}}>
          <div className={[styles.cellContainer].join(' ')}>
            {cell.render('Cell')}
          </div>
        </td>
      );
  }
}


export default {
  getColumns,
  renderCell,
  getData,
  getExcelData,
  getExcelColumns,
  columnsFixtures: columns,
};
